import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

function computeRemainingFree(items) {
  let muleQty = 0;
  let sockQty = 0;
  for (const item of items) {
    if (item.handle === 'the-everyday-mule') muleQty += item.quantity;
    if (item.handle === 'the-everyday-sock') sockQty += item.quantity;
  }
  return Math.max(muleQty - sockQty, 0);
}

function updateFreeBadge(section, items) {
  const badge = section.querySelector('[data-sock-free-badge]');
  if (!badge) return;

  const remainingFree = computeRemainingFree(items);
  if (remainingFree > 0) {
    badge.textContent = '1 pair included free';
    badge.classList.add('sock-addon__badge--free');
    badge.classList.remove('sock-addon__badge--paid');
  } else {
    badge.textContent = badge.dataset.paidLabel || '';
    badge.classList.remove('sock-addon__badge--free');
    badge.classList.add('sock-addon__badge--paid');
  }
}

function toggleSection(section) {
  const body = section.querySelector('.sock-addon__body');
  const toggleBtn = section.querySelector('.sock-addon__toggle');
  const isOpen = section.classList.contains('is-open');

  if (isOpen) {
    body.style.display = 'none';
    section.classList.remove('is-open');
    toggleBtn?.setAttribute('aria-expanded', 'false');
  } else {
    body.style.display = 'block';
    section.classList.add('is-open');
    toggleBtn?.setAttribute('aria-expanded', 'true');
  }
}

function swapImage(select) {
  const option = select.selectedOptions[0];
  const img = select.closest('.sock-addon__product')?.querySelector('.sock-addon__img');
  if (option?.dataset.img && img) img.src = option.dataset.img;
}

async function refreshCart() {
  const cartItemsComponent = document.querySelector('cart-items-component');

  if (cartItemsComponent) {
    await customElements.whenDefined('cart-items-component');
    return cartItemsComponent.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

async function addSock(button) {
  const productInfo = button.closest('.sock-addon__product-info');
  const select = productInfo?.querySelector('.sock-addon__select');
  const section = button.closest('.sock-addon');
  const productId = section?.dataset.productId;

  if (!select?.value) return;

  const variantId = select.value;
  const originalText = button.dataset.originalText || button.textContent;
  button.dataset.originalText = originalText;

  button.textContent = '...';
  button.disabled = true;

  // cart-items-component only does a live morph-in-place re-render when the
  // response carries rendered section HTML for it - otherwise it falls back
  // to a much less reliable path. Ask for that HTML explicitly, exactly like
  // product-form.js does for the main add-to-cart button.
  const cartItemComponentsSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  // Notify the cart drawer / header cart count up front, same contract product-form.js uses,
  // but only *resolve* this promise once the add is actually confirmed below.
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferredEventPromise.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: cartItemComponentsSectionIds.join(','),
      }),
    }).then((res) => res.json());

    // Shopify's cart/add.js returns a `status` field on failure even when the
    // HTTP status itself can look successful - this is the same check product-form.js uses.
    if (response.status) {
      throw new Error(response.message || 'Add to cart failed');
    }

    const cart = await refreshCart();

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'sock-addon',
        sourceId: section?.id || 'sock-addon-section',
        itemCount: 1,
        productId,
        sections: response.sections,
        didError: false,
      },
    });

    updateFreeBadge(section, cart.items);

    // Only flip to "Added" now that the line item is confirmed in the cart.
    button.textContent = 'Added ✓';
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 2500);
  } catch (error) {
    deferredEventPromise.reject(error);
    document.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Add to cart failed',
        code: 'INVALID',
      })
    );

    button.textContent = 'Probeer opnieuw';
    button.disabled = false;
    setTimeout(() => {
      button.textContent = originalText;
    }, 2000);
  }
}

function init() {
  const section = document.getElementById('sock-addon-section');
  if (!section || section.dataset.sockAddonBound) return;
  section.dataset.sockAddonBound = 'true';

  section.querySelector('.sock-addon__toggle')?.addEventListener('click', () => toggleSection(section));

  section.querySelectorAll('.sock-addon__select').forEach((select) => {
    select.addEventListener('change', () => swapImage(select));
  });

  section.querySelectorAll('.sock-addon__btn').forEach((button) => {
    button.addEventListener('click', () => addSock(button));
  });

  // Keep the free/paid badge accurate even when the cart changes elsewhere
  // (e.g. removing a Mule or Sock line item from the cart drawer).
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.items) updateFreeBadge(section, detail.items);
      })
      .catch(() => {});
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
